import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import {
  ErrorOutline,
  LinkOff,
  AccessTime,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export type EmptyStateType =
  | 'not-found'
  | 'invalid-link'
  | 'expired-link'
  | 'server-error'
  | 'network-error';

interface EmptyStateProps {
  type: EmptyStateType;
  title?: string;
  message?: string;
  showBackButton?: boolean;
  onRetry?: () => void;
}

const getEmptyStateConfig = (type: EmptyStateType) => {
  const configs = {
    'not-found': {
      icon: ErrorOutline,
      color: 'text.secondary' as const,
      titleKey: 'errors.notFound',
      messageKey: 'errors.notFoundMessage',
    },
    'invalid-link': {
      icon: LinkOff,
      color: 'warning.main' as const,
      titleKey: 'errors.invalidLink',
      messageKey: 'errors.invalidLinkMessage',
    },
    'expired-link': {
      icon: AccessTime,
      color: 'warning.main' as const,
      titleKey: 'errors.expiredLink',
      messageKey: 'errors.expiredLinkMessage',
    },
    'server-error': {
      icon: ErrorIcon,
      color: 'error.main' as const,
      titleKey: 'errors.serverError',
      messageKey: 'errors.serverErrorMessage',
    },
    'network-error': {
      icon: ErrorIcon,
      color: 'error.main' as const,
      titleKey: 'errors.networkError',
      messageKey: 'errors.networkErrorMessage',
    },
  };

  return configs[type];
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  title,
  message,
  showBackButton = true,
  onRetry,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const config = getEmptyStateConfig(type);
  const IconComponent = config.icon;

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 8,
          px: 2,
        }}
      >
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <IconComponent
            sx={{
              fontSize: 64,
              color: config.color,
            }}
          />
        </Box>

        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ mb: 2, fontWeight: 600 }}
        >
          {title || t(config.titleKey)}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 400 }}
        >
          {message || t(config.messageKey)}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {onRetry && (
            <Button
              variant="outlined"
              size="large"
              onClick={onRetry}
              sx={{ minWidth: 120 }}
            >
              {t('common.retry')}
            </Button>
          )}

          {showBackButton && (
            <Button
              variant="contained"
              size="large"
              onClick={handleBackToHome}
              sx={{ minWidth: 120 }}
            >
              {t('errors.backToHome')}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default EmptyState;
